import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import CssBaseline from '@mui/material/CssBaseline'
import DownloadIcon from '@mui/icons-material/SimCardDownload'
import { ThemeProvider } from '@mui/material/styles'
import { useParams } from 'react-router-dom'

import * as cp from '../../comps'
import * as gs from '../../../app/gStyles'
import generatePDF from '../../helpers/generatePDF'
import { reportTheme } from '../../constants'

//@ts-ignore
import styles from './Contract.module.css'

export default React.memo(function Contract() {
  const { customerID } = useParams<'customerID'>()
  if (!customerID) {
    throw new ReferenceError(
      `No customerID inside <Contract />. Contact dev dpt.`,
    )
  }

  //#region signature
  const [signatureOpenVal, , signatureOpenToggle] = r.useBool(false)
  const handleWheelEvent = (e: any) => {
    e.preventDefault()
  }
  const handleKeyEvent = (e: any) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
    }
  }
  React.useEffect(() => {
    if (signatureOpenVal) {
      document.body.addEventListener('wheel', handleWheelEvent)
      document.body.addEventListener('keydown', handleKeyEvent)
    }
    return () => {
      document.body.removeEventListener('wheel', handleWheelEvent)
      document.body.removeEventListener('keydown', handleKeyEvent)
    }
  }, [signatureOpenVal])
  const handleSign = React.useCallback(
    (base64: string) => {
      const signatureTimestamp = c.normalizeTimestampToMs(Date.now())
      localStorage.setItem('UPS/publicSign/signature', base64)
      localStorage.setItem(
        'UPS/publicSign/timestamp',
        signatureTimestamp.toString(),
      )

      Promise.all([
        c.getDatabase().ref(`Contract/${customerID}`).update({
          customerSigned: true,
          customerSignedDate: signatureTimestamp,
        }),

        c.getDatabase().ref(`ContractSignature/${customerID}`).set({
          signature: base64,
          timestamp: signatureTimestamp,
        }),
      ])
        .then(() => {
          alert('Contract signed!')
        })
        .catch((e) => {
          alert(`Error signing contract. Contact Nirvana. ${c.processErr(e)}`)
        })
    },
    [customerID],
  )
  //#endregion

  const [contract, setContract] =
    React.useState<c.SharedContractNirvana | null>(null)

  React.useEffect(() => {
    const contractRef = c.getDatabase().ref(`Contract/${customerID}`)

    contractRef.on('value', (snapshot) => {
      const data = snapshot.val() as c.SharedContractNirvana | null

      setContract(data)
    })

    return () => {
      contractRef.off()
    }
  }, [customerID])

  const contractCompanyFull = React.useMemo(
    (): c.ContractCompany => ({
      address1: contract?.contractCompanyAddress1 || '',
      address2: contract?.contractCompanyAddress2 || '',
      email: contract?.contractCompanyEmail || '',
      logo: contract?.contractCompanyLogo || '',
      longName: contract?.contractCompanyLongName || '',
      phoneNumber: contract?.contractCompanyPhoneNumber || '',
      roofingLicense: contract?.contractCompanyRoofingLicense || '',
      shortName: contract?.contractCompanyShortName || '',
      states: contract?.contractCompanyStates || '',
    }),
    [
      contract?.contractCompanyAddress1,
      contract?.contractCompanyAddress2,
      contract?.contractCompanyEmail,
      contract?.contractCompanyLogo,
      contract?.contractCompanyLongName,
      contract?.contractCompanyPhoneNumber,
      contract?.contractCompanyRoofingLicense,
      contract?.contractCompanyShortName,
      contract?.contractCompanyStates,
    ],
  )

  const contractCompanyOpts = React.useMemo(
    () => [contractCompanyFull],
    [contractCompanyFull],
  )

  const signatureOpenToggleHandle = React.useCallback(() => {
    if (contract?.customerSigned) {
      alert('Contract already signed')
      return
    }
    signatureOpenToggle()
  }, [contract?.customerSigned, signatureOpenToggle])

  const downloadIconStyle = React.useMemo(
    () => ({
      color: c.companyColors[contract?.state || ''],
      cursor: 'pointer',
      fontSize: '60px',
    }),
    [contract?.state],
  )
  const downloadContainerStyle = React.useMemo(
    () => ({
      ...gs.deadCenter,
      backgroundColor: '#ffffff',
      borderColor: c.companyColors[contract?.state || ''],
      borderRadius: '50%',
      borderStyle: 'solid' as const,
      borderWidth: 4,
      bottom: 50,
      boxShadow: '0 8px 20px rgba(2,6,23,0.2)',
      height: 96,
      position: 'fixed' as const,
      right: 20,
      width: 96,
    }),
    [contract?.state],
  )

  const [downloadGenerationIsVal, downloadGenerationIsSet] =
    React.useState(false)

  const [downloadGenerationErrVal, downloadGenerationErrSet] = React.useState<
    null | string
  >(null)
  const downloadGenerationErrDismiss = React.useCallback(() => {
    downloadGenerationErrSet(null)
  }, [])
  const downloadHandle = React.useCallback(async () => {
    if (!contract?.customerSigned) {
      alert('PDF will download but contract has not been signed')
    }
    downloadGenerationErrSet(null)
    downloadGenerationIsSet(true)
    await new Promise((r) => requestAnimationFrame(r))
    try {
      await generatePDF(contract?.customerName || '')
    } catch (e) {
      downloadGenerationErrSet(c.processErr(e))
    } finally {
      downloadGenerationIsSet(false)
    }
  }, [contract?.customerName, contract?.customerSigned])

  if (!contract?.contractCompanyShortName) {
    return (
      <div
        aria-busy="true"
        aria-live="assertive"
        className={styles['splash']}
        role="status"
      >
        <div className={styles['splash-card']}>
          <CircularProgress size={56} />
          <p className={styles['splash-title']}>Loading...</p>
          <p className={styles['splash-message']}>
            Please wait while we prepare the PDF to be downloaded
          </p>
        </div>
      </div>
    )
  }

  const localStorageSignTimestamp = localStorage.getItem(
    'UPS/publicSign/timestamp',
  )

  return (
    <>
      <ThemeProvider theme={reportTheme}>
        <CssBaseline />

        <div className={styles['container']}>
          <cp.NirvanaContractView
            amountFinance={contract?.amountFinance || ''}
            amountFinanceOnChange={c.emptyFn}
            amountRemaining={contract?.amountRemaining || ''}
            amountRemainingOnChange={c.emptyFn}
            amountUpfront={contract?.amountUpfront || ''}
            amountUpfrontOnChange={c.emptyFn}
            contractCompanyFull={contractCompanyFull}
            contractCompanyOpts={contractCompanyOpts}
            customerAddress={contract?.customerAddress || ''}
            customerEmail={contract?.customerEmail || ''}
            customerName={contract?.customerName || ''}
            customerPhone={contract?.customerPhone || ''}
            customerPhoneAlt={contract?.customerPhoneAlt || ''}
            customerSignature={
              contract?.customerSigned
                ? localStorage.getItem('UPS/publicSign/signature') || asterisks
                : ''
            }
            customerSignatureDate={
              contract?.customerSigned
                ? typeof localStorageSignTimestamp === 'string'
                  ? c.parseInt(localStorageSignTimestamp)
                  : contract?.customerSignedDate
                : undefined
            }
            isPDF={downloadGenerationIsVal}
            repName={contract?.repName || ''}
            repSignature={asterisks}
            repSignatureDate={undefined}
            rows={contract?.rows || c.EMPTY_ARRAY}
            state={contract?.state || ''}
            toggleCompanySignatureModal={c.emptyFn}
            toggleSignatureModal={signatureOpenToggleHandle}
            totalCostCustomer={contract?.totalCostCustomer || 0}
          />
        </div>
      </ThemeProvider>

      <cp.Modal open={signatureOpenVal} onRequestClose={signatureOpenToggle}>
        <cp.SignatureControlled
          onRequestClose={signatureOpenToggle}
          onSign={handleSign}
        />
      </cp.Modal>

      <div style={downloadContainerStyle}>
        <DownloadIcon onClick={downloadHandle} sx={downloadIconStyle} />
      </div>

      {(downloadGenerationIsVal || downloadGenerationErrVal) && (
        <div
          aria-busy={downloadGenerationIsVal}
          aria-live="assertive"
          className={styles['splash']}
          role="status"
        >
          <div className={styles['splash-card']}>
            {downloadGenerationIsVal ? (
              <>
                <CircularProgress size={56} />
                <p className={styles['splash-title']}>Generating PDF</p>
                <p className={styles['splash-message']}>
                  Please wait while we prepare the customer report.
                </p>
              </>
            ) : (
              <>
                <p className={styles['splash-title']}>PDF generation failed</p>
                <p className={styles['splash-error']}>
                  {downloadGenerationErrVal ||
                    'Unexpected error while generating the PDF.'}
                </p>
                <Button
                  onClick={downloadGenerationErrDismiss}
                  variant="contained"
                >
                  Dismiss
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
})

export const rootSurfaceContract: c.Surface = 'pasteboard'

const asterisks =
  'data:image/png;base64,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'
